import { useEffect, useState } from "react";
import { Box, Text } from "@twilio-paste/core";
import { useTheme } from "@twilio-paste/theme";

import {
  MessageStatus,
  ReduxMessage,
} from "../../store/reducers/messageListReducer";
import SendingIcon from "../icons/Sending";
import DeliveredIcon from "../icons/Delivered";
import ReadIcon from "../icons/Read";
import FailedIcon from "../icons/Failed";
import BellMuted from "../icons/BellMuted";

import { NOTIFICATION_LEVEL } from "../../constants";
import { SetSidType, SetUnreadMessagesType } from "../../types";
import { getMessageStatus } from "../../api";

import { getLastMessageTime } from "./../../utils/timestampUtils";

import { ReduxConversation } from "../../store/reducers/convoReducer";
import { ReduxParticipant } from "../../store/reducers/participantsReducer";

interface SingleConvoProps {
  convoId: string;
  setSid: SetSidType;
  currentConvoSid: string;
  lastMessage: string;
  myMessage: ReduxMessage | false;
  unreadMessagesCount: number;
  convo: ReduxConversation;
  updateUnreadMessages: SetUnreadMessagesType;
  onClick: () => void;
  participants: ReduxParticipant[];
  messages: ReduxMessage[];
  typingInfo: string[];
  use24hTimeFormat: boolean;
  isMobile?: boolean;
}

const ConversationView: React.FC<SingleConvoProps> = (
  props: SingleConvoProps
) => {
  const {
    convo,
    convoId,
    myMessage,
    lastMessage,
    unreadMessagesCount,
    use24hTimeFormat,
    isMobile = false,
  } = props;
  const [backgroundColor, setBackgroundColor] = useState();
  const title = convo.friendlyName ?? convo.sid;
  const theme = useTheme();
  const textColor =
    unreadMessagesCount > 0
      ? theme.textColors.colorText
      : theme.textColors.colorTextIcon;
  const muted = convo.notificationLevel === NOTIFICATION_LEVEL.MUTED;

  const [lastMsgStatus, setLastMsgStatus] = useState("");
  const time = getLastMessageTime(props.messages, use24hTimeFormat);

  useEffect(() => {
    if (props.currentConvoSid === convo.sid) {
      setBackgroundColor(theme.backgroundColors.colorBackgroundStrong);
      return;
    }
    setBackgroundColor(theme.backgroundColors.colorBackgroundRowStriped);
  }, [props.currentConvoSid, convo.sid]);

  useEffect(() => {
    if (myMessage && !props.typingInfo.length) {
      getMessageStatus(myMessage, props.participants).then((statuses) => {
        if (statuses[MessageStatus.Read]) {
          setLastMsgStatus(MessageStatus.Read);
          return;
        }
        if (statuses[MessageStatus.Delivered]) {
          setLastMsgStatus(MessageStatus.Delivered);
          return;
        }
        if (statuses[MessageStatus.Failed]) {
          setLastMsgStatus(MessageStatus.Failed);
          return;
        }
        if (statuses[MessageStatus.Sending]) {
          setLastMsgStatus(MessageStatus.Sending);
          return;
        }
      });
    }
  }, [convo, myMessage, lastMessage, props.participants, props.typingInfo]);

  return (
    <Box
      style={{
        paddingTop: isMobile ? 11 : 14,
        paddingBottom: isMobile ? 11 : 14,
        paddingLeft: isMobile ? 14 : 16,
        paddingRight: isMobile ? 14 : 16,
        cursor: "pointer",
        backgroundColor: backgroundColor,
        minHeight: isMobile ? 72 : undefined,
        borderBottom: isMobile ? "1px solid rgba(57, 71, 98, 0.08)" : undefined,
      }}
      id={convoId}
      className="name"
      onMouseOver={() => {
        if (convo.sid === props.currentConvoSid) {
          return;
        }
        setBackgroundColor(theme.backgroundColors.colorBackgroundStrong);
      }}
      onMouseOut={() => {
        if (convo.sid === props.currentConvoSid) {
          return;
        }
        setBackgroundColor(theme.backgroundColors.colorBackgroundRowStriped);
      }}
      onClick={props.onClick}
    >
      <Box
        style={{
          backgroundColor: backgroundColor,
        }}
      >
        <Box display="flex">
          <Box
            style={{
              flex: 1,
              minWidth: 0,
              fontWeight: theme.fontWeights.fontWeightSemibold,
              fontSize: isMobile ? 15 : 14,
              color: muted
                ? theme.textColors.colorTextInverseWeaker
                : theme.textColors.colorText,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: isMobile ? "20px" : undefined,
            }}
          >
            {muted ? <BellMuted /> : null}
            <span style={{ verticalAlign: "top", paddingLeft: muted ? 4 : 0 }}>
              {title}
            </span>
          </Box>
          {unreadMessagesCount > 0 && (
            <Box paddingLeft={isMobile ? "space20" : "space30"}>
              <Box
                backgroundColor="colorBackgroundBrandStronger"
                color="colorTextInverse"
                fontFamily="fontFamilyText"
                fontWeight="fontWeightBold"
                fontSize={isMobile ? "fontSize20" : "fontSize30"}
                lineHeight={isMobile ? "lineHeight20" : "lineHeight30"}
                paddingLeft={isMobile ? "space20" : "space30"}
                paddingRight={isMobile ? "space20" : "space30"}
                style={{
                  borderRadius: 999,
                  minWidth: isMobile ? 22 : undefined,
                  textAlign: "center",
                  opacity: muted ? 0.2 : 1,
                }}
              >
                {unreadMessagesCount}
              </Box>
            </Box>
          )}
        </Box>
        <Box
          style={{
            paddingTop: isMobile ? 3 : 4,
            color: textColor,
            fontWeight: theme.fontWeights.fontWeightNormal,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: isMobile ? 8 : 0,
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "row",
              flex: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              alignItems: "center",
            }}
          >
            {!props.typingInfo.length && lastMsgStatus ? (
              <Box
                display="flex"
                paddingRight={isMobile ? "space10" : "space20"}
                alignItems="center"
              >
                {lastMsgStatus === MessageStatus.Sending && props.myMessage && (
                  <SendingIcon />
                )}
                {lastMsgStatus === MessageStatus.Delivered &&
                  props.myMessage && <DeliveredIcon />}
                {lastMsgStatus === MessageStatus.Failed && props.myMessage && (
                  <FailedIcon color="#D61F1F" />
                )}
                {lastMsgStatus === MessageStatus.Read && props.myMessage && (
                  <ReadIcon />
                )}
              </Box>
            ) : null}
            <Box
              style={{
                flex: 1,
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {lastMsgStatus === MessageStatus.Failed ? (
                <Text
                  as="span"
                  color="colorTextError"
                  fontWeight={"fontWeightSemibold"}
                  fontSize={isMobile ? "fontSize30" : undefined}
                >
                  {"Message failed to send"}
                </Text>
              ) : (
                <Text
                  as="span"
                  color="colorTextWeak"
                  fontSize={isMobile ? "fontSize30" : undefined}
                >
                  {lastMessage}
                </Text>
              )}
            </Box>
          </Box>
          <Box style={{ whiteSpace: "nowrap", paddingLeft: isMobile ? 0 : 4 }}>
            <Text
              as="span"
              color={"colorTextWeak"}
              fontSize={isMobile ? "fontSize10" : "fontSize20"}
              fontWeight={"fontWeightSemibold"}
            >
              {time}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ConversationView;
